import useAlias from '@alias';
import useGetWidgetConfigs from '@hooks/api/queries/widgets/useGetWidgetConfigs';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Snackbar, SnackbarCloseReason } from '@mui/material';
import { GridBaseColDef } from '@mui/x-data-grid/internals';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColType,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import CollectionToolbar from '@src/components/Home/Collections/CollectionToolbar';
import deviceSx from '@src/components/Home/Collections/configs/DeviceSx';
import widgetConfigBaseColumns from '@src/components/Home/Collections/configs/widgetConfigBaseColumns';
import getCollectionsInitialState from '@src/components/Home/Collections/utils/getCollectionsInitialState';
import WidgetConfigDialog from '@src/components/Home/Collections/WidgetConfigs/WidgetConfigDialog';
import { isActionRole } from '@src/services/auth';
import { UserRole } from '@src/services/auth/roles';
import { CollectionPayload, CollectionTypes } from '@src/types/Collection';
import { WidgetConfigBase, WidgetConfigExtended } from '@src/types/Widget';
import storeConnector from '@store/storeConnector';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CollectionWrapper from '../CollectionWrapper';

declare module '@mui/x-data-grid-pro' {
  interface ToolbarPropsOverrides {
    collectionType?: CollectionTypes;
    setOpen: Dispatch<SetStateAction<boolean>>;
    setJsonPayload: Dispatch<SetStateAction<CollectionPayload | null>>;
  }
}

function WidgetConfigCollection({ role }: { role: UserRole }) {
  const alias = useAlias();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rows, setRows] = useState<WidgetConfigBase[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);
  const apiRef = useGridApiRef();

  const [jsonPayload, setJsonPayload] = useState<CollectionPayload | null>(
    null,
  );
  const { data, isFetching, isPending, isSuccess, error, isRefetching } =
    useGetWidgetConfigs();

  const handleClose = (
    _: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setConfirmOpen(false);
  };

  const handleCloneClick = (id: GridRowId) => () => {
    const row = apiRef.current?.getRow(id);
    if (row && apiRef.current) {
      const { id: _, createdAt, updatedAt, ...clonedRow } = row;
      setJsonPayload({
        widgets: [
          {
            ...clonedRow,
            internalId: '',
          },
        ],
      });
      setOpen(true);
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    if (apiRef.current !== undefined) {
      setJsonPayload(apiRef.current.getRow(id));
    }
    setOpen(true);
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    if (apiRef.current !== undefined) {
      setJsonPayload({
        ...(apiRef.current.getRow(id) as CollectionPayload),
        remove: true,
      });
    }
    setOpen(true);
  };

  const handleCancelClick = (id?: GridRowId) => () => {
    if (id !== undefined) {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      });
    }
    setOpen(false);
    setJsonPayload(null);
  };

  const columns = [
    ...widgetConfigBaseColumns(alias),
    ...(isActionRole(role) && !process.env.VITE_READ_ONLY_MODE
      ? [
          {
            field: 'actions',
            type: 'actions' as GridColType,
            headerName: alias('Actions'),
            description: alias('Actions'),
            width: 120,
            cellClassName: 'actions',
            getActions: ({ id }: GridRowParams<{ id: string }>) => [
              <GridActionsCellItem
                key='clone'
                icon={<CopyAllIcon />}
                label={alias('Clone')}
                className='textPrimary'
                onClick={handleCloneClick(id)}
                color='inherit'
              />,
              <GridActionsCellItem
                key='edit'
                icon={<EditIcon />}
                label={alias('Edit')}
                className='textPrimary'
                onClick={handleEditClick(id)}
                color='inherit'
              />,
              <GridActionsCellItem
                key='delete'
                icon={<DeleteIcon />}
                label={alias('Delete')}
                onClick={handleDeleteClick(id)}
                color='inherit'
              />,
            ],
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (error) {
      console.error(error);
    }
    if (isSuccess && data !== null) {
      setRows(data?.map((device) => ({ ...device })));
    }
  }, [isSuccess, data, error]);

  return (
    <CollectionWrapper>
      <div className='cell block-container flex-1 overflow-y-auto'>
        <Snackbar
          open={confirmOpen}
          autoHideDuration={4000}
          onClose={handleClose}
          sx={deviceSx.confirm}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleClose}
            severity='success'
            variant='filled'
            sx={deviceSx.confirm.alert}
          >
            {alias('Point config changes saved')}
          </Alert>
        </Snackbar>
        <DataGridPro
          rows={rows}
          apiRef={apiRef}
          checkboxSelection
          rowModesModel={rowModesModel}
          onRowModesModelChange={setRowModesModel}
          onRowSelectionModelChange={setRowSelectionModel}
          rowSelectionModel={rowSelectionModel}
          initialState={getCollectionsInitialState()}
          columns={columns as GridBaseColDef[]}
          getRowId={({ id }) => id}
          loading={!isRefetching && (isFetching || isPending)}
          slots={{
            toolbar: CollectionToolbar,
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
            toolbar: {
              collectionType: CollectionTypes.WIDGETS,
              setOpen,
              setJsonPayload,
            },
          }}
          localeText={{
            toolbarColumns: alias('Columns'),
            toolbarFilters: alias('Filters'),
            toolbarExport: alias('Export'),
            toolbarQuickFilterPlaceholder: alias('Search...'),
          }}
        />

        {open && jsonPayload && (
          <WidgetConfigDialog
            jsonData={jsonPayload as WidgetConfigExtended}
            setOpen={setOpen}
            handleCancel={handleCancelClick}
            setRowModesModel={setRowModesModel}
            setJsonPayload={setJsonPayload}
          />
        )}
      </div>
    </CollectionWrapper>
  );
}

export default storeConnector(WidgetConfigCollection, {
  user: ['role'],
});
